/**
 * Brand-override layers for the current identity.
 *
 * The system DEFAULT brand for every protocol/standard lives in the
 * `@authe/protocol-ui` catalog. This hook supplies the overrides layered on top
 * (resolved by the package's `useBrand` with `user > tenant > default`
 * precedence):
 *  - USER layer — persisted in `ScopedStorage` (scoped to the session identity)
 *    via `BrandPrefsStore`, so a user's tweaks survive reloads and never leak
 *    across tenants.
 *  - TENANT layer — owned by the catalog field-overrides service. Wired here
 *    once that resource exposes brand overrides; until then it's empty and
 *    resolution falls through to the user pref + system default.
 *
 * The user layer is written from Settings › General › Protocol appearance
 * (`useBrandOverrideEditor`). A write has to reach every chip already on screen,
 * and `localStorage` only announces a change to OTHER tabs, so same-tab writers
 * bump a revision the read hook subscribes to; other tabs arrive through the
 * store's own `storage` subscription. The layer is checked on every read
 * (`BrandPrefsStore.getOverrides`), because storage is cast rather than typed.
 */
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useSessionIdentityKey } from "@authe/ui-core/session";
import { usePermissions } from "@authe/ui-core/permissions";
import { brandsFromProtocolSummaries } from "@authe/protocol-ui";
import { useProtocolsSource } from "@/api/adapters/analytics";
import { createScopedStorage } from "@authe/fe-commons/storage";
import { useOptionalTenant } from "@authe/ui-core/tenant";
import {
  createBrandPrefsStore,
  type BrandOverrideEntry,
  type BrandOverrideLayers,
  type BrandOverrideMap,
  type BrandPrefsStore,
} from "@authe/protocol-ui";
import { APPLICATION_SLUG, RESOURCES } from "@/config";

/** One identity-keyed catalogue read, shared with the entity references view. */
export function useRegistryBrands() {
  const source = useProtocolsSource();
  const identity = useSessionIdentityKey();
  const { can } = usePermissions();
  const query = useQuery({
    queryKey: ["entity-protocol-catalogue", identity],
    enabled: Boolean(identity) && can(RESOURCES.metadataRegistry, "analytics"),
    queryFn: () => source.list(),
    staleTime: 60_000,
  });
  return React.useMemo(() => brandsFromProtocolSummaries(query.data ?? []), [query.data]);
}

/** Every mounted reader of the user layer, so a same-tab write reaches them all. */
const readers = new Set<() => void>();
let revision = 0;

function announce(): void {
  revision += 1;
  // Copied first: a reader that unmounts during the walk must not reshape the set.
  for (const reader of [...readers]) reader();
}

/** The user-layer store for the session identity; a new one when that changes. */
function useBrandPrefs(): BrandPrefsStore {
  const tenant = useOptionalTenant();
  return React.useMemo(
    () => createBrandPrefsStore(createScopedStorage({
      app: APPLICATION_SLUG,
      customerId: tenant?.customerId ?? null,
      orgId: tenant?.orgId ?? null,
      tenantId: tenant?.tenantId ?? null,
      userId: tenant?.userId ?? null,
    })),
    [tenant?.customerId, tenant?.orgId, tenant?.tenantId, tenant?.userId],
  );
}

/** The stored user layer, re-read whenever this tab or another one writes it. */
function useUserBrandOverrides(store: BrandPrefsStore): BrandOverrideMap {
  const subscribe = React.useCallback((onChange: () => void) => {
    readers.add(onChange);
    const offOtherTabs = store.subscribe(() => { revision += 1; onChange(); });
    return () => { readers.delete(onChange); offOtherTabs(); };
  }, [store]);
  const seen = React.useSyncExternalStore(subscribe, () => revision, () => revision);
  // `seen` is the dependency: the map is re-read from storage when it moves.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useMemo(() => store.getOverrides(), [store, seen]);
}

export function useBrandOverrides(): BrandOverrideLayers {
  const user = useUserBrandOverrides(useBrandPrefs());
  return React.useMemo<BrandOverrideLayers>(() => ({ user }), [user]);
}

export interface BrandOverrideEditor {
  /** The user's own overrides, by protocol id. */
  overrides: BrandOverrideMap;
  /**
   * Store one protocol's override, or forget it with `null`. `false` when the
   * browser refused the write (private window, blocked site data, a full store):
   * nothing changed, and the caller says so rather than show a change that would
   * be gone on reload.
   */
  save: (protocolId: string, override: BrandOverrideEntry | null) => boolean;
}

/** Read and write the user layer. Every chip on screen follows a successful write. */
export function useBrandOverrideEditor(): BrandOverrideEditor {
  const store = useBrandPrefs();
  const overrides = useUserBrandOverrides(store);
  const save = React.useCallback((protocolId: string, override: BrandOverrideEntry | null): boolean => {
    const stored = override ? store.setOverride(protocolId, override) : store.removeOverride(protocolId);
    if (stored) announce();
    return stored;
  }, [store]);
  return React.useMemo(() => ({ overrides, save }), [overrides, save]);
}
